import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import type { IsoItemApplicability, SoaControlItem } from './types';
import {
  SOA_APPLICABILITY_LABEL,
  SOA_STATUS_LABEL,
  SOA_STATUS_ORDER,
  SOA_THEME_LABEL,
  SOA_THEME_ORDER,
  implementationStatusBadgeClass,
} from './soaUtils';

interface SoaControlsGridProps {
  controls: SoaControlItem[];
  canEdit: boolean;
  isSuperadmin: boolean;
  onApplicabilityChange: (
    control: SoaControlItem,
    applicability: IsoItemApplicability,
    justification?: string,
  ) => void | Promise<void>;
  onStatusChange: (
    control: SoaControlItem,
    status: SoaControlItem['implementationStatus'],
  ) => void | Promise<void>;
  onEvidenceChange: (control: SoaControlItem, evidenceNote: string | null) => void | Promise<void>;
  onOrgContextChange?: (
    control: SoaControlItem,
    payload: { meaningForOrganization?: string | null; implementationParticulars?: string | null },
  ) => void | Promise<void>;
}

const APPLICABILITY_VALUES: IsoItemApplicability[] = ['sin_definir', 'aplica', 'no_aplica'];

export function SoaControlsGrid({
  controls,
  canEdit,
  isSuperadmin,
  onApplicabilityChange,
  onStatusChange,
  onEvidenceChange,
  onOrgContextChange,
}: SoaControlsGridProps) {
  const [justificationTarget, setJustificationTarget] = useState<SoaControlItem | null>(null);
  const [justification, setJustification] = useState('');
  const [justificationError, setJustificationError] = useState<string | null>(null);
  const [savingJustification, setSavingJustification] = useState(false);

  const grouped = SOA_THEME_ORDER.map((theme) => ({
    theme,
    label: SOA_THEME_LABEL[theme],
    items: controls.filter((ctrl) => ctrl.theme === theme),
  })).filter((group) => group.items.length > 0);

  const openJustificationDialog = (control: SoaControlItem) => {
    setJustificationTarget(control);
    setJustification(control.justification ?? '');
    setJustificationError(null);
  };

  const closeJustificationDialog = () => {
    if (savingJustification) return;
    setJustificationTarget(null);
    setJustification('');
    setJustificationError(null);
  };

  const submitNoAplica = async () => {
    if (!justificationTarget) return;
    const trimmed = justification.trim();
    if (!trimmed) {
      setJustificationError('La justificación es obligatoria para marcar No aplica.');
      return;
    }

    setSavingJustification(true);
    try {
      await onApplicabilityChange(justificationTarget, 'no_aplica', trimmed);
      setJustificationTarget(null);
      setJustification('');
      setJustificationError(null);
    } finally {
      setSavingJustification(false);
    }
  };

  return (
    <div className="space-y-8">
      {grouped.map((group) => (
        <section key={group.theme} className="space-y-3">
          <h2 className="text-lg font-semibold">{group.label}</h2>
          <div className="overflow-x-auto border border-border rounded-lg">
            <table className="min-w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-3 py-2 text-left">Ref.</th>
                  <th className="px-3 py-2 text-left">Control</th>
                  <th className="px-3 py-2 text-left">Aplicabilidad</th>
                  <th className="px-3 py-2 text-left">Justificación</th>
                  <th className="px-3 py-2 text-left">Estado impl.</th>
                  <th className="px-3 py-2 text-left">Notas / evidencia</th>
                  {isSuperadmin ? (
                    <th className="px-3 py-2 text-left">Contexto org</th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {group.items.map((ctrl) => (
                  <tr
                    key={ctrl.id}
                    className="border-t border-border align-top"
                    data-testid={`soa-row-${ctrl.controlRef}`}
                  >
                    <td className="px-3 py-2 font-mono whitespace-nowrap">{ctrl.controlRef}</td>
                    <td className="px-3 py-2">
                      <div>{ctrl.title}</div>
                      {ctrl.linkedRisks.length > 0 ? (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {ctrl.linkedRisks.map((risk) => (
                            <Link
                              key={risk.id}
                              to={`/riesgos/${risk.id}`}
                              className="text-xs text-primary underline-offset-4 hover:underline"
                              title={risk.title}
                            >
                              {risk.code}
                            </Link>
                          ))}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-3 py-2">
                      {canEdit ? (
                        <div className="flex flex-col gap-1 text-xs">
                          {APPLICABILITY_VALUES.map((value) => (
                            <label key={value} className="flex items-center gap-1.5">
                              <input
                                type="radio"
                                name={`apply-${ctrl.id}`}
                                checked={ctrl.applicability === value}
                                data-testid={`soa-applicability-${ctrl.controlRef}-${value}`}
                                onChange={() => {
                                  if (value === 'no_aplica') {
                                    openJustificationDialog(ctrl);
                                    return;
                                  }
                                  onApplicabilityChange(ctrl, value, '');
                                }}
                              />
                              {SOA_APPLICABILITY_LABEL[value]}
                            </label>
                          ))}
                        </div>
                      ) : (
                        <span>{SOA_APPLICABILITY_LABEL[ctrl.applicability]}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 max-w-xs text-xs text-muted-foreground">
                      {ctrl.applicability === 'no_aplica' ? ctrl.justification : '—'}
                    </td>
                    <td className="px-3 py-2">
                      {canEdit ? (
                        <select
                          className="input text-sm"
                          value={ctrl.implementationStatus}
                          data-testid={`soa-status-${ctrl.controlRef}`}
                          onChange={(e) =>
                            onStatusChange(
                              ctrl,
                              e.target.value as SoaControlItem['implementationStatus'],
                            )
                          }
                          disabled={ctrl.applicability !== 'aplica'}
                        >
                          {SOA_STATUS_ORDER.map((value) => (
                            <option key={value} value={value}>
                              {SOA_STATUS_LABEL[value]}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span
                          className={`inline-flex rounded px-2 py-0.5 text-xs ${implementationStatusBadgeClass(ctrl.implementationStatus)}`}
                        >
                          {SOA_STATUS_LABEL[ctrl.implementationStatus]}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 min-w-[8rem]">
                      {canEdit ? (
                        <input
                          type="text"
                          className="input text-xs w-full"
                          defaultValue={ctrl.evidenceNote ?? ''}
                          placeholder="Notas…"
                          onBlur={(e) => {
                            const value = e.target.value.trim();
                            if (value !== (ctrl.evidenceNote ?? '')) {
                              onEvidenceChange(ctrl, value || null);
                            }
                          }}
                        />
                      ) : (
                        <span className="text-xs text-muted-foreground">
                          {ctrl.evidenceNote ?? '—'}
                        </span>
                      )}
                    </td>
                    {isSuperadmin && onOrgContextChange ? (
                      <td className="px-3 py-2 min-w-[10rem]">
                        <textarea
                          className="input text-xs w-full min-h-[3rem] mb-1"
                          defaultValue={ctrl.meaningForOrganization ?? ''}
                          placeholder="Significado…"
                          onBlur={(e) =>
                            onOrgContextChange(ctrl, {
                              meaningForOrganization: e.target.value.trim() || null,
                            })
                          }
                        />
                        <textarea
                          className="input text-xs w-full min-h-[3rem]"
                          defaultValue={ctrl.implementationParticulars ?? ''}
                          placeholder="Particularidades…"
                          onBlur={(e) =>
                            onOrgContextChange(ctrl, {
                              implementationParticulars: e.target.value.trim() || null,
                            })
                          }
                        />
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ))}
      <Dialog
        open={Boolean(justificationTarget)}
        onOpenChange={(open) => {
          if (!open) closeJustificationDialog();
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Justificar No aplica</DialogTitle>
            <DialogDescription>
              Indicá por qué este control no aplica a la organización.
            </DialogDescription>
          </DialogHeader>
          {justificationTarget ? (
            <div className="space-y-3">
              <div className="rounded-md bg-muted p-3 text-sm">
                <p className="font-mono text-xs text-muted-foreground">
                  {justificationTarget.controlRef}
                </p>
                <p className="font-medium">{justificationTarget.title}</p>
              </div>
              <textarea
                className="input min-h-[7rem] w-full text-sm"
                value={justification}
                disabled={savingJustification}
                onChange={(event) => {
                  setJustification(event.target.value);
                  if (justificationError) setJustificationError(null);
                }}
                placeholder="Ej.: No aplica porque la organización no opera instalaciones físicas propias..."
                autoFocus
              />
              {justificationError ? (
                <p className="text-sm text-destructive">{justificationError}</p>
              ) : null}
            </div>
          ) : null}
          <DialogFooter>
            <button
              type="button"
              className="btn btn-outline"
              disabled={savingJustification}
              onClick={closeJustificationDialog}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={savingJustification}
              onClick={() => void submitNoAplica()}
            >
              {savingJustification ? 'Guardando…' : 'Guardar'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
